// Реквизиты выплаты и отметка факта перевода. Формы — зеркало contracts/openapi.
//
// Полного номера карты в этих типах нет и быть не должно: сервер отдаёт только последние
// четыре цифры, и это не урезанный показ, а весь объём данных, который существует на стороне
// клиента. Номер целиком принимается один раз, при подаче заявления, и больше не возвращается.

import { request } from '@/shared/api/client';
import { queryString } from '@/shared/api/query';
import type { ListResponse } from '@/shared/api/types';

export type PayoutMethod = 'card' | 'sbp';

export interface ApplicationOut {
  id: string;
  employee_id: string;
  employee_name: string;
  method: PayoutMethod;
  application_date?: string | null;
  bank_name?: string | null;
  /** Последние четыре цифры карты: именно они уходят в строку ведомости и печатную форму. */
  card_last4?: string | null;
  phone?: string | null;
  /** Заполнено, если перевод третьему лицу: в бланке заказчика такое поле есть. */
  recipient_name?: string | null;
  bik?: string | null;
  corr_account?: string | null;
  settlement_account?: string | null;
  doc_file_id?: string | null;
  /** Действующее заявление ровно одно, остальные — история. */
  is_active: boolean;
  created_at: string;
}

export interface ApplicationIn {
  employee_id: string;
  method: PayoutMethod;
  application_date?: string | null;
  bank_name?: string | null;
  card_number?: string | null;
  phone?: string | null;
  recipient_name?: string | null;
  bik?: string | null;
  corr_account?: string | null;
  settlement_account?: string | null;
  doc_file_id?: string | null;
}

export interface FactOut {
  id: string;
  payroll_employee_id: string;
  employee_id: string;
  employee_name: string;
  amount: number;
  paid_at: string;
  method_used?: PayoutMethod | null;
  last4?: string | null;
  proof_file_id?: string | null;
  reversal_of_id?: string | null;
  reversal_basis?: string | null;
}

export interface FactIn {
  payroll_employee_id: string;
  amount: number;
  method_used?: PayoutMethod | null;
  proof_file_id?: string | null;
  /** День перевода «2026-10-11». Пусто — сегодня. Будущим числом сервер откажет. */
  paid_on?: string | null;
}

export function fetchApplications(
  params: { employee_id?: string; only_active?: boolean } = {},
): Promise<ListResponse<ApplicationOut>> {
  return request<ListResponse<ApplicationOut>>(
    `/api/v1/payouts/applications${queryString({ ...params })}`,
  );
}

export function createApplication(body: ApplicationIn): Promise<ApplicationOut> {
  return request<ApplicationOut>('/api/v1/payouts/applications', { method: 'POST', body });
}

export function fetchFacts(
  params: { employee_id?: string; payroll_employee_id?: string } = {},
): Promise<ListResponse<FactOut>> {
  return request<ListResponse<FactOut>>(`/api/v1/payouts/facts${queryString({ ...params })}`);
}

/** Отметить выплаченной ВСЮ ведомость. Сумма не спрашивается: платят посчитанное. */
export interface BulkFactIn {
  payroll_id: string;
  proof_file_id?: string | null;
  /** День перевода «2026-10-11». Пусто — сегодня. */
  paid_on?: string | null;
}

export interface BulkFactRow {
  payroll_employee_id: string;
  employee_id: string;
  employee_name: string;
  amount: number;
  skipped_reason: string | null;
}

export interface BulkFactOut {
  created: number;
  skipped: number;
  /** Сколько отмечено выплаченным, копейки. */
  total_amount: number;
  rows: BulkFactRow[];
}

/**
 * Сторно отметки о выплате: обратная запись со ссылкой на исходную.
 *
 * Правки денежной записи не существует нигде в продукте, и здесь тоже: ошиблись человеком,
 * суммой или перевод не ушёл — оформляется встречная запись. До 06.09.2026 экран советовал
 * именно это, а операции не существовало: отметку нельзя было отменить ничем, и человек
 * навсегда пропадал из списка «кому перевести».
 *
 * Право у руководства и администратора: отмечает выплату финансовый отдел, отменяет другой.
 */
export function reverseFact(factId: string, basis: string): Promise<FactOut> {
  return request<FactOut>(`/api/v1/payouts/facts/${factId}/reverse`, {
    method: 'POST',
    body: { basis },
  });
}

export function markPaidBulk(body: BulkFactIn): Promise<BulkFactOut> {
  return request<BulkFactOut>('/api/v1/payouts/facts/bulk', { method: 'POST', body });
}

/**
 * Отметить выплату проведённой.
 *
 * Ключ операции обязателен на этой стороне, хотя сервер принимает и без него: отметка — денежная
 * запись, и повтор без ключа заводит вторую. Второй факт по тому же человеку бывает законным
 * (перевели не всё сразу), поэтому отличить повтор от настоящей второй выплаты может только он.
 */
export function markPaid(body: FactIn, idempotencyKey: string): Promise<FactOut> {
  return request<FactOut>('/api/v1/payouts/facts', { method: 'POST', body, idempotencyKey });
}
